/**
 * orval mutator — the single fetch implementation every generated operation
 * routes through. Delegates to the shared transport in `../http.ts` so the
 * generated client inherits auth headers, the runtime API base URL, 401
 * handling, cross-origin credentials, and `ApiError` parsing for free.
 *
 * orval (client: 'react-query', custom mutator) calls
 * `apiFetch<T>({ url, method, params, data, headers, signal })` and expects
 * the parsed body back (`Promise<T>`). `request()` prepends the API base, so
 * `url` stays bare (e.g. `/agents`).
 *
 * Org scoping: `request()` auto-injects `x-organization-id` for the active
 * org, but an `x-organization-id` passed in `headers` (i.e. by an org-scoped
 * facade hook in `./agents.ts`) overrides it — that explicit pass-through is
 * what makes the org id a compile-time requirement in the facade.
 */
import { request, type RequestOptions } from '../http.js';

export interface ApiFetchConfig {
  url: string;
  method: string;
  params?: Record<string, unknown>;
  data?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  responseType?: string;
}

function toQueryString(params: Record<string, unknown> | undefined): string {
  if (!params) return '';
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

export const apiFetch = async <T>(config: ApiFetchConfig, options?: RequestOptions): Promise<T> => {
  const { url, method, params, data, headers, signal } = config;
  const init: RequestInit = { method: method.toUpperCase() };
  if (data !== undefined) init.body = typeof data === 'string' ? data : JSON.stringify(data);
  if (headers) init.headers = headers;
  if (signal) init.signal = signal;
  return request<T>(`${url}${toQueryString(params)}`, init, options);
};

export default apiFetch;
